import { useCallback, useEffect, useRef, useState } from "react";

/** Audio in, words out. The caller decides where it goes (the workspace API); this package never does. */
export type Transcribe = (audio: Blob, durationMs: number) => Promise<string>;
export type RecorderState = "idle" | "recording" | "transcribing";
/** Why the microphone could not be used. Each has one plain message; playback is unaffected by any of them. */
export type RecorderProblem = "denied" | "no_microphone" | "unsupported" | "failed" | "quiet" | "unclear";

/** Thrown by a `Transcribe` when the words came back but were not an answer (the API's "didn't come through"). */
export class NotHeard extends Error { constructor(message = "not heard") { super(message); this.name = "NotHeard"; } }

/** Loud enough to be speech, on the 0..1 meter scale (about -32 dBFS), for at least this many meter frames. A
 * recording with less is not sent: transcription models invent text, often in another language, for near-silence. */
export const SPEECH_LEVEL = 0.1;
export const SPEECH_FRAMES = 4;

export const MAX_RECORDING_MS = 120_000;
/** The first of these the browser can record is used: Chrome and Firefox take webm, Safari mp4. */
export const CODECS = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"];

export const PROBLEM_TEXT: Record<RecorderProblem, string> = {
  denied: "The microphone is blocked for this site. Allow it in the browser's site settings to dictate; you can still type.",
  no_microphone: "No microphone was found. You can still type.",
  unsupported: "This browser can't record audio here. You can still type.",
  failed: "That didn't come through. Try again, or type your answer.",
  quiet: "I couldn't hear anything, so nothing was sent.",
  unclear: "That didn't come through clearly, so nothing was added. Try again, closer to the microphone.",
};

export function pickMimeType(supported: (type: string) => boolean = (t) => MediaRecorder.isTypeSupported(t)): string | null {
  return CODECS.find((t) => { try { return supported(t); } catch { return false; } }) ?? null;
}

function problemOf(e: unknown): RecorderProblem {
  const name = e instanceof DOMException || e instanceof Error ? e.name : "";
  if (name === "NotAllowedError" || name === "SecurityError") return "denied";
  if (name === "NotFoundError" || name === "OverconstrainedError") return "no_microphone";
  return "failed";
}

/**
 * Press to start, press to stop (no hold-to-talk: it fails on phones). On stop the recording is sent to `transcribe`
 * and the words are handed to `onText`; the audio is dropped once sent. Stops by itself after two minutes.
 * `level` (0..1) is the input loudness while recording, for a meter.
 */
export function useRecorder(transcribe: Transcribe, onText: (text: string) => void) {
  const [state, setState] = useState<RecorderState>("idle");
  const [problem, setProblem] = useState<RecorderProblem | null>(null);
  const [level, setLevel] = useState(0);
  // the input actually in use: on a Mac an iPhone nearby can take over as the microphone without anyone noticing
  const [device, setDevice] = useState<string | null>(null);
  const recorder = useRef<MediaRecorder | null>(null);
  const stream = useRef<MediaStream | null>(null);
  const cleanup = useRef<() => void>(() => undefined);
  const speech = useRef<() => boolean | null>(() => null);
  const limit = useRef<ReturnType<typeof setTimeout> | null>(null);
  const alive = useRef(true);
  const latest = useRef({ transcribe, onText });
  latest.current = { transcribe, onText };

  const release = useCallback(() => {
    if (limit.current) clearTimeout(limit.current);
    limit.current = null;
    cleanup.current();
    cleanup.current = () => undefined;
    stream.current?.getTracks().forEach((t) => t.stop());
    stream.current = null;
    setLevel(0);
  }, []);

  const stop = useCallback(() => {
    if (recorder.current?.state === "recording") recorder.current.stop();
  }, []);

  const start = useCallback(async () => {
    if (state !== "idle") return;
    setProblem(null);
    if (typeof MediaRecorder === "undefined" || !navigator.mediaDevices?.getUserMedia) { setProblem("unsupported"); return; }
    const mimeType = pickMimeType();
    if (!mimeType) { setProblem("unsupported"); return; }
    let media: MediaStream;
    try {
      media = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (e) {
      setProblem(problemOf(e));
      return;
    }
    if (!alive.current) { media.getTracks().forEach((t) => t.stop()); return; }
    stream.current = media;
    setDevice(media.getAudioTracks()[0]?.label || null);
    const m = meter(media, setLevel);
    cleanup.current = m.stop;
    speech.current = m.heardSpeech;
    const rec = new MediaRecorder(media, { mimeType });
    const parts: Blob[] = [];
    const began = performance.now();
    rec.ondataavailable = (e) => { if (e.data.size) parts.push(e.data); };
    rec.onstop = async () => {
      const durationMs = Math.round(performance.now() - began);
      const heard = speech.current();   // read before release() tears the meter down
      release();
      recorder.current = null;
      const audio = new Blob(parts, { type: mimeType.split(";")[0] });
      parts.length = 0;
      if (!audio.size) { if (alive.current) setState("idle"); return; }
      if (heard === false) { if (alive.current) { setProblem("quiet"); setState("idle"); } return; }
      if (alive.current) setState("transcribing");
      try {
        const text = (await latest.current.transcribe(audio, durationMs)).trim();
        if (alive.current && text) latest.current.onText(text);
      } catch (e) {
        if (alive.current) setProblem(e instanceof NotHeard ? "unclear" : "failed");
      } finally {
        if (alive.current) setState("idle");
      }
    };
    recorder.current = rec;
    rec.start();
    setState("recording");
    limit.current = setTimeout(stop, MAX_RECORDING_MS);
  }, [state, release, stop]);

  const toggle = useCallback(() => { if (state === "recording") stop(); else void start(); }, [state, start, stop]);

  // Set on mount as well as cleared on unmount: React may mount, unmount and mount again (StrictMode does in
  // development), and a flag only ever cleared would leave the recorder thinking the page had gone.
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      if (recorder.current?.state === "recording") recorder.current.stop();
      release();
    };
  }, [release]);

  return { start, stop, toggle, state, level, device, problem, clearProblem: () => setProblem(null) };
}

/**
 * Input loudness, sampled once a frame, and whether anything loud enough to be speech came in. `heardSpeech` is null
 * when it cannot be told (no Web Audio, or the meter's context never started, as iOS may do without a fresh
 * gesture): the recording is then sent as before rather than refused on a silent meter.
 */
function meter(media: MediaStream, onLevel: (v: number) => void): { stop: () => void; heardSpeech: () => boolean | null } {
  const none = { stop: () => undefined, heardSpeech: () => null };
  if (typeof AudioContext === "undefined") return none;
  let ctx: AudioContext;
  try { ctx = new AudioContext(); } catch { return none; }
  void ctx.resume().catch(() => undefined);
  const analyser = ctx.createAnalyser();
  analyser.fftSize = 512;
  ctx.createMediaStreamSource(media).connect(analyser);
  const samples = new Uint8Array(analyser.fftSize);
  let frame = 0;
  let measured = 0;
  let loud = 0;
  const tick = () => {
    if (ctx.state === "running") {
      analyser.getByteTimeDomainData(samples);
      let sum = 0;
      for (const s of samples) sum += ((s - 128) / 128) ** 2;
      const level = Math.min(1, Math.sqrt(sum / samples.length) * 4);
      measured += 1;
      if (level >= SPEECH_LEVEL) loud += 1;
      onLevel(level);
    }
    frame = requestAnimationFrame(tick);
  };
  frame = requestAnimationFrame(tick);
  return {
    stop: () => { cancelAnimationFrame(frame); void ctx.close().catch(() => undefined); },
    heardSpeech: () => (measured < 10 ? null : loud >= SPEECH_FRAMES),
  };
}
